import React, { useState } from 'react';
import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components';
import {
    Checkmark16Regular,
    DocumentPdf16Regular,
    DocumentText16Regular,
    FullScreenMaximize16Regular,
    FullScreenMinimize16Regular,
    LockClosed12Regular,
    Share16Regular,
    SpinnerIos16Regular,
} from '@fluentui/react-icons';
import { toast } from 'react-toastify';
import styles from './index.module.css';
import { getSectionVisibility, isSectionPrivate, splitPastedContentIntoTitleAndBody } from './journalUtils';

const TITLE_MAX_LENGTH = 45;

export const DocumentHeader = ({ entryTitle, ownerName, selectedSection, onTitleChange, onTitlePaste, onMoveToBody }) => {
    const visibility = getSectionVisibility(selectedSection);
    const showLock = isSectionPrivate(selectedSection);

    const handlePaste = e => {
        const html = e.clipboardData?.getData('text/html') || '';
        const text = e.clipboardData?.getData('text/plain') || '';
        const { shouldSplit, title, bodyHtml } = splitPastedContentIntoTitleAndBody(html, text);

        if (!shouldSplit) return;

        e.preventDefault();
        onTitlePaste?.(title, bodyHtml);
    };

    const handleKeyDown = e => {
        const input = e.target;
        const value = entryTitle || '';
        const selStart = input.selectionStart ?? value.length;
        const selEnd = input.selectionEnd ?? value.length;
        const hasSelection = selStart !== selEnd;

        // Enter always moves into the body editor
        if (e.key === 'Enter') {
            e.preventDefault();
            onMoveToBody?.('');
            return;
        }

        const isPrintable = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
        const atLimit = value.length >= TITLE_MAX_LENGTH;

        // At max length with no selection — continue typing in the body
        if (atLimit && isPrintable && !hasSelection) {
            e.preventDefault();
            onMoveToBody?.(e.key);
        }
    };

    return (
        <div className={styles.documentHeader}>
            <div className={styles.documentMeta}>
                <div className={styles.documentMetaOwner}>
                    <span className={styles.metaLabel}>Owner:</span>
                    <span className={styles.metaOwnerName}>{ownerName || '—'}</span>
                </div>
                <span className={styles.metaDot}>•</span>
                <div className={styles.documentMetaVisibility}>
                    {showLock && <LockClosed12Regular color="var(--Neutral-Gray-6)" />}
                    <span>{visibility}</span>
                </div>
            </div>
            <input
                type="text"
                className={styles.documentTitleInput}
                value={entryTitle || ''}
                onChange={e => onTitleChange?.(e.target.value.slice(0, TITLE_MAX_LENGTH))}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                maxLength={TITLE_MAX_LENGTH}
                placeholder="Untitled"
                aria-label="Entry title"
            />
            <hr className={styles.documentDivider} aria-hidden="true" />
        </div>
    );
};

const SaveStatusIndicator = ({ saveStatus }) => {
    if (saveStatus === 'saved') {
        return (
            <div className={styles.saveStatus} aria-live="polite">
                <Checkmark16Regular color="var(--Neutral-Gray-6)" />
                <span>Saved</span>
            </div>
        );
    }

    // unsaved + saving → spinner only (no "Unsaved" / "Saving…" text)
    return (
        <div className={styles.saveStatus} aria-live="polite" aria-label="Saving">
            <SpinnerIos16Regular className={styles.saveStatusSpinner} color="var(--Neutral-Gray-6)" />
        </div>
    );
};

const EditorChrome = ({ sectionName, entryTitle, saveStatus, isMaximized, onToggleMaximize, onExportNote, children }) => {
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = async format => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            await onExportNote?.(format);
        } catch (error) {
            console.error('Export failed:', error);
            toast.error(`Failed to export as ${String(format).toUpperCase()}`);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className={styles.editorPane}>
            <div className={styles.editorTopBar}>
                <div className={styles.breadcrumbs}>
                    <span className={`${styles.breadcrumbMuted} ${styles.breadcrumbSection}`}>{sectionName || 'Journal'}</span>
                    <span className={styles.breadcrumbMuted}>/</span>
                    <span className={styles.breadcrumbActive}>{entryTitle || 'Untitled'}</span>
                </div>
                <div className={styles.editorStatusGroup}>
                    <SaveStatusIndicator saveStatus={saveStatus} />
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <button
                                type="button"
                                className={styles.iconBtn}
                                aria-label="Export note"
                                title="Export note"
                                disabled={isExporting}>
                                {isExporting ? (
                                    <SpinnerIos16Regular className={styles.saveStatusSpinner} color="var(--Neutral-Gray-6)" />
                                ) : (
                                    <Share16Regular color="var(--Neutral-Gray-6)" />
                                )}
                            </button>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem icon={<DocumentPdf16Regular />} disabled={isExporting} onClick={() => handleExport('pdf')}>
                                    Export as PDF
                                </MenuItem>
                                <MenuItem icon={<DocumentText16Regular />} disabled={isExporting} onClick={() => handleExport('docx')}>
                                    Export as DOCX
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                    <button
                        type="button"
                        className={styles.iconBtn}
                        onClick={onToggleMaximize}
                        aria-label={isMaximized ? 'Exit fullscreen' : 'Maximize editor'}
                        title={isMaximized ? 'Exit fullscreen' : 'Maximize editor'}>
                        {isMaximized ? (
                            <FullScreenMinimize16Regular color="var(--Neutral-Gray-6)" />
                        ) : (
                            <FullScreenMaximize16Regular color="var(--Neutral-Gray-6)" />
                        )}
                    </button>
                </div>
            </div>

            <div className={styles.editorMain}>{children}</div>
        </div>
    );
};

export default EditorChrome;
